/**
 * Audio AI Processing Configuration
 *
 * This file configures the audio processing providers.
 * Change provider names to switch between implementations.
 */

/**
 * Languages supported by Moonshine STT
 * These get Moonshine Tiny (~190MB, 5x faster than Whisper)
 */
export const MOONSHINE_LANGUAGES = ['en', 'ar', 'zh', 'ja', 'ko', 'uk', 'vi'];

/**
 * Audio processing configuration
 */
export const audioConfig = {
  /**
   * Noise Suppression settings
   */
  noiseSuppression: {
    enabled: true,
    provider: 'rnnoise', // 'rnnoise' | 'none'
  },

  /**
   * Voice Activity Detection settings
   */
  vad: {
    enabled: true,
    provider: 'silero', // 'silero' | 'webrtc' | 'energy'
  },

  /**
   * Speech-to-Text settings
   * Provider is selected automatically based on user's preferred language:
   * - Moonshine: for languages in MOONSHINE_LANGUAGES
   * - Whisper: for all other languages (100+ supported)
   */
  stt: {
    enabled: true,
    // Provider auto-selected based on language
    moonshineLanguages: MOONSHINE_LANGUAGES,
    // Model variants
    moonshineModel: 'onnx-community/moonshine-tiny-onnx', // ~190MB
    whisperModel: 'onnx-community/whisper-base', // ~290MB
  },

  /**
   * Device compatibility policy
   * - 'error-blocking': Show error and block if device not compatible (recommended)
   * - 'fallback': Fall back to cloud services if available
   * - 'auto': Use best available option silently
   */
  devicePolicy: 'error-blocking',

  /**
   * Device requirements per feature
   */
  requirements: {
    noiseSuppression: {
      wasmSimd: true,
      webgpu: false,
      memory: 0, // No specific requirement
    },
    vad: {
      wasmSimd: true, // Silero VAD needs SIMD
      webgpu: false,
      memory: 0,
    },
    stt: {
      wasmSimd: true, // ONNX runtime needs SIMD
      webgpu: true,   // Required for fast inference
      memory: 4,      // 4GB minimum for model loading
    },
  },
};

/**
 * Get STT provider for a given language
 * @param {string} language - Language code (e.g., 'en', 'it')
 * @returns {Object} - { provider: string, model: string, size: string }
 */
export function getSTTProvider(language) {
  if (audioConfig.stt.moonshineLanguages.includes(language)) {
    return {
      provider: 'moonshine',
      model: audioConfig.stt.moonshineModel,
      size: '~190MB',
      reason: 'Optimized for ' + language,
    };
  }

  return {
    provider: 'whisper',
    model: audioConfig.stt.whisperModel,
    size: '~290MB',
    reason: 'Full language support (100+ languages)',
  };
}

/**
 * Get all requirements for current configuration
 * @returns {Object} - Combined requirements
 */
export function getAggregatedRequirements() {
  const reqs = { wasmSimd: false, webgpu: false, memory: 0 };

  if (audioConfig.noiseSuppression.enabled && audioConfig.noiseSuppression.provider !== 'none') {
    const nsReqs = audioConfig.requirements.noiseSuppression;
    reqs.wasmSimd = reqs.wasmSimd || nsReqs.wasmSimd;
    reqs.webgpu = reqs.webgpu || nsReqs.webgpu;
    reqs.memory = Math.max(reqs.memory, nsReqs.memory);
  }

  if (audioConfig.vad.enabled && audioConfig.vad.provider !== 'energy') {
    const vadReqs = audioConfig.requirements.vad;
    reqs.wasmSimd = reqs.wasmSimd || vadReqs.wasmSimd;
    reqs.webgpu = reqs.webgpu || vadReqs.webgpu;
    reqs.memory = Math.max(reqs.memory, vadReqs.memory);
  }

  if (audioConfig.stt.enabled) {
    const sttReqs = audioConfig.requirements.stt;
    reqs.wasmSimd = reqs.wasmSimd || sttReqs.wasmSimd;
    reqs.webgpu = reqs.webgpu || sttReqs.webgpu;
    reqs.memory = Math.max(reqs.memory, sttReqs.memory);
  }

  return reqs;
}

export default audioConfig;
